import React, { useState, useEffect, useRef } from "react";
import { Input, Button, Tag, Spin, Tooltip, Badge, Typography, Modal, Row, Col, Card } from "antd";
import { PlusOutlined, LeftOutlined, RightOutlined, DownOutlined, SearchOutlined, OrderedListOutlined, ClockCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import ServiceStatus from "../../ServiceStatus";
import GeneralServices from "../../../../pages/GeneralServices";
import '../../../../scss/GeneralTable.scss'

const { Text, Title } = Typography;

export default function GeneralTable(props) {
	const { openOrders, loading, userId } = props;
	const role = localStorage.getItem("role");

	const location = useLocation();
	const navigate = useNavigate();
	const isInMarketplace = location.pathname === "/marketplace/orders";

	const [orders, setOrders] = useState([]);
	const [internalLoading, setInternalLoading] = useState(false);
	const [tableData, setTableData] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [searchedValue, setSearchedValue] = useState("");
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [expandedRows, setExpandedRows] = useState([]);
	
	const [sortConfig, setSortConfig] = useState({
		key: 'created_at',
		direction: 'descend'
	});

	const tableContainerRef = useRef(null);

	useEffect(() => {
		if (openOrders) {
			setOrders(openOrders);
		}
	}, [openOrders]);

	useEffect(() => {
		if (orders && orders.length > 0) {
			const formattedData = orders.map((order, index) => {
				let latestServiceUpdate = null;

				if (order.services && order.services.length > 0) {
					latestServiceUpdate = order.services
						.map(service => moment(service.updated_at || service.updatedAt).valueOf())
						.sort((a, b) => b - a)[0];
				} else {
					latestServiceUpdate = moment(order.created_at || order.createdAt).valueOf();
				}

				return {
					key: order.orderId || order.id || index,
					order_id: (order.orderId || order.id)?.toString() || `orden-${index}`,
					order_title: order.title || '',
					client_bussiness: order.client_bussiness || order.client_name || '',
					updated_at: latestServiceUpdate,
					created_at: moment(order.created_at || order.createdAt).valueOf(),
					days_of_age: moment(order.created_at || order.createdAt).fromNow(),
					services: order.services || [],
					status: order.status || 'active'
				};
			});
			
			const sortedData = sortData(formattedData, sortConfig);
			setTableData(sortedData);
			applyFilters(sortedData, searchedValue);
		} else {
			setTableData([]);
			setFilteredData([]);
		}
	}, [orders]);

	const sortData = (data, config) => {
		if (!config.key) return data;

		return [...data].sort((a, b) => {
			let aValue = a[config.key];
			let bValue = b[config.key];

			if (config.key === 'created_at' || config.key === 'updated_at') {
				if (config.direction === 'descend') {
					return bValue - aValue; 
				} else {
					return aValue - bValue; 
				}
			}

			if (typeof aValue === 'string' && typeof bValue === 'string') {
				aValue = aValue.toLowerCase();
				bValue = bValue.toLowerCase();
			}

			if (aValue < bValue) {
				return config.direction === 'ascend' ? -1 : 1;
			}
			if (aValue > bValue) {
				return config.direction === 'ascend' ? 1 : -1;
			}
			return 0;
		});
	};

	const handleSort = (key) => {
		setSortConfig(prevConfig => {
			const newConfig = {
				key,
				direction: prevConfig.key === key && prevConfig.direction === 'descend' ? 'ascend' : 'descend'
			};
			
			const sortedTableData = sortData(tableData, newConfig);
			setTableData(sortedTableData);
			applyFilters(sortedTableData, searchedValue);
			
			return newConfig;
		});
		setCurrentPage(1);
	};

	const getSortIcon = (key) => {
		if (sortConfig.key !== key) return null;
		return sortConfig.direction === 'descend' ? <ArrowDownOutlined /> : <ArrowUpOutlined />;
	};

	const applyFilters = (data, search) => {
		let result = [...data];

		if (search && search.trim() !== "") {
			const searchLower = search.toLowerCase().trim();
			result = result.filter(item =>
				item.order_id.toLowerCase().includes(searchLower) ||
				item.order_title.toLowerCase().includes(searchLower) ||
				item.client_bussiness.toLowerCase().includes(searchLower)
			);
		}

		setFilteredData(result);
		setCurrentPage(1);
		setExpandedRows([]);
	};

	const handleSearch = () => {
		applyFilters(tableData, searchedValue);
	};

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchedValue(value);
		applyFilters(tableData, value);
	};

	const handleReset = () => {
		setSearchedValue("");
		const defaultConfig = {
			key: 'created_at',
			direction: 'descend'
		};
		setSortConfig(defaultConfig);
		const sortedTableData = sortData(tableData, defaultConfig);
		setTableData(sortedTableData);
		applyFilters(sortedTableData, "");
	};

	const handleNewOrder = () => {
		setIsModalVisible(true);
	};

	const handleModalClose = () => {
		setIsModalVisible(false);
	};

	const handleOrderCreated = () => {
		setIsModalVisible(false);
		window.location.reload();
	};

	const handlePageSizeChange = (newSize) => {
		setPageSize(newSize);
		setCurrentPage(1);
		if (tableContainerRef.current) {
			tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const handleNextPage = () => {
		if (currentPage < Math.ceil(filteredData.length / pageSize)) {
			setCurrentPage(prev => prev + 1);
			if (tableContainerRef.current) {
				tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
			}
		}
	};

	const handlePrevPage = () => {
		if (currentPage > 1) {
			setCurrentPage(prev => prev - 1);
			if (tableContainerRef.current) {
				tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
			}
		}
	};

	const handleRowClick = (orderId) => {
		if (!orderId) return;
		navigate(`/orders/details/new/${orderId}`);
	};

	const toggleServicesDetails = (orderKey) => {
		setExpandedRows(prev =>
			prev.includes(orderKey)
				? prev.filter(id => id !== orderKey)
				: [...prev, orderKey]
		);
	};

	const getDisplayRange = () => {
		const start = ((currentPage - 1) * pageSize) + 1;
		const end = Math.min(start + filteredData.length - 1, start + pageSize - 1);
		return { start, end };
	};

	const paginatedData = filteredData.slice(
		(currentPage - 1) * pageSize,
		currentPage * pageSize
	);

	const hasActiveFilters = searchedValue !== "";

	const isLoading = loading !== undefined ? loading : internalLoading;

	const getTitle = () => {
		if (isLoading) return "Cargando órdenes...";
		if (userId && !openOrders) {
			if (role === '4') return "Mis Órdenes";
			if (role === '6') return "Órdenes para Proveedor";
			return "Órdenes Asignadas";
		}
		return "Órdenes Abiertas";
	};

	const displayRange = getDisplayRange();

	return (
		<div className="general-table-container">
			<div className="orders-card">
				<Row gutter={[24, 16]}>
					<Col xs={24}>
						<Title level={3}>{getTitle()}</Title>
					</Col>
					<Col xs={24}>
						<Row gutter={[16, 16]}>
							<Col xs={24} md={16} lg={18}>
								<Input
									placeholder="Buscar por número de orden, título o cliente..."
									value={searchedValue}
									onChange={handleSearchChange}
									prefix={<SearchOutlined />}
									allowClear
									onClear={handleReset}
									size="large"
								/>
							</Col>

							{role === "4" && !isInMarketplace && (
								<Col xs={24} md={8} lg={6}>
									<Button
										type="primary"
										onClick={handleNewOrder}
										icon={<PlusOutlined />}
										size="large"
										block
										style={{ background: '#52c41a', borderColor: '#52c41a' }}
									>
										Nueva orden
									</Button>
								</Col>
							)}
						</Row>
					</Col>

					{/* Tabla */}
					<Col xs={24}>
						<div className="table-container" ref={tableContainerRef}>
							<table className="styled-table">
								<thead>
									<tr>
										<th className="header-cell orden"># Orden</th>
										<th className="header-cell servicios">Ver servicios</th>
										<th className="header-cell titulo">Título</th>
										<th className="header-cell cliente">Cliente</th>
										<th 
											className="header-cell actualizacion sortable-header"
											onClick={() => handleSort('updated_at')}
										>
											<span className="sortable-header-content">
												Última Actualización {getSortIcon('updated_at')}
											</span>
										</th>
										<th 
											className="header-cell antiguedad sortable-header"
											onClick={() => handleSort('created_at')}
										>
											<span className="sortable-header-content">
												Fecha de Creación {getSortIcon('created_at')}
											</span>
										</th>
									</tr>
								</thead>
								<tbody>
									{isLoading ? (
										<tr>
											<td colSpan="6" className="empty-state-cell">
												<Spin size="large" />
												<p>Cargando órdenes...</p>
											</td>
										</tr>
									) : paginatedData.length === 0 ? (
										<tr>
											<td colSpan="6" className="empty-state-cell">
												{hasActiveFilters ? (
													<div className="no-results">
														<p>No se encontraron órdenes con el filtro aplicado</p>
														<button
															className="clear-filters-btn empty-state-btn"
															onClick={handleReset}
														>
															Mostrar todas las órdenes
														</button>
													</div>
												) : (
													<p>{userId ? "No hay órdenes asignadas disponibles" : "No hay órdenes disponibles"}</p>
												)}
											</td>
										</tr>
									) : (
										paginatedData.map(record => {
											const isExpanded = expandedRows.includes(record.key);
											return (
											<React.Fragment key={record.key}>
												<tr
													className="table-row"
													onClick={() => handleRowClick(record.order_id)}
												>
													<td className="body-cell orden">
														<div className="order-info">
															{record.order_id ? (
																<Link
																	to={`/orders/details/new/${record.order_id}`}
																	className="order-link"
																	onClick={(e) => e.stopPropagation()}
																>
																	{record.order_id}
																</Link>
															) : (
																<Tag color="volcano">unavailable</Tag>
															)}
															<div
																className="services-count"
															>
																<span>{record.services?.length || 0} servicios</span>
															</div>
														</div>
													</td>
													<td className="body-cell servicios">
														<div
															className="services-toggle"
															onClick={(e) => {
																e.stopPropagation();
																toggleServicesDetails(record.key);
															}}
															title={isExpanded ? "Ocultar servicios" : "Ver servicios"}
														>
															<span>Ver</span>
															{isExpanded ? <DownOutlined className="expand-icon" /> : <RightOutlined className="expand-icon" />}
														</div>
													</td>
													<td className="body-cell titulo">
														<span className="order-title">{record.order_title.toUpperCase()}</span>
													</td>
													<td className="body-cell cliente">
														<span className="client-name">{record.client_bussiness.toUpperCase()}</span>
													</td>
													<td className="body-cell actualizacion">
														<Tooltip title={moment(record.updated_at).format('DD/MM/YYYY HH:mm')}>
															{moment(record.updated_at).fromNow()}
														</Tooltip>
													</td>
													<td className="body-cell antiguedad">
														{moment(record.created_at).format('DD/MM/YYYY')}
													</td>
												</tr>

												{isExpanded && (
													<tr className="expanded-row">
														<td colSpan="6" className="expanded-cell">
															<div className="expanded-content">
																<div className="services-section">
																	<div className="section-title">
																		<OrderedListOutlined style={{ marginRight: 8 }} />
																		Servicios de la orden
																		<Badge
																			count={record.services?.length || 0}
																			showZero
																			style={{ marginLeft: 8, backgroundColor: '#0f5197' }}
																		/>
																	</div>

																	<div className="services-list">
																		{record.services && record.services.length > 0 ? (
																			record.services.map((service, idx) => (
																				<div
																					key={idx}
																					className="service-item"
																					onClick={() => handleRowClick(record.order_id)}
																				>
																					<Card size="small" className="service-card">
																						<Row gutter={[16, 8]}>
																							<Col xs={24} md={5}>
																								<Text strong className="service-label"># Servicio</Text>
																								<div className="service-value">
																									{service.serviceId || service.service_id || service.id || 'N/A'}
																								</div>
																							</Col>
																							<Col xs={24} md={5}>
																								<Text strong className="service-label">Título</Text>
																								<div className="service-value">{service.title}</div>
																							</Col>
																							<Col xs={24} md={6}>
																								<Text strong className="service-label">Descripción</Text>
																								<div className="service-value">{service.description}</div>
																							</Col>
																							<Col xs={24} md={4}>
																								<Text strong className="service-label">Actualización</Text>
																								<div className="service-value">
																									<Tooltip title={moment(service.updated_at || new Date()).format('DD/MM/YYYY HH:mm')}>
																										{moment(service.updated_at || new Date()).fromNow()}
																									</Tooltip>
																								</div>
																							</Col>
																							<Col xs={24} md={4}>
																								<Text strong className="service-label">Status</Text>
																								<div className="service-value">
																									<ServiceStatus status={service.status} />
																								</div>
																							</Col>
																						</Row>
																					</Card>
																				</div>
																			))
																		) : (
																			<div className="no-services">
																				No hay servicios en esta orden
																			</div>
																		)}
																	</div>

																	<div className="expanded-footer">
																		<Text type="secondary">
																			<ClockCircleOutlined style={{ marginRight: 4 }} />
																			Última actualización: {moment(record.updated_at).fromNow()}
																		</Text>
																	</div>
																</div>
															</div>
														</td>
													</tr>
												)}
											</React.Fragment>
											);
										})
									)}
								</tbody>
							</table>
						</div>
					</Col>

					{/* Paginación */}
					{filteredData.length > 0 && (
						<Col xs={24}>
							<div className="main-pagination-container">
								<div className="pagination-info">
									<span>
										{hasActiveFilters
											? `Mostrando ${paginatedData.length} orden${paginatedData.length !== 1 ? 'es' : ''} filtrada${paginatedData.length !== 1 ? 's' : ''}`
											: `Mostrando órdenes ${displayRange.start}-${displayRange.end} de ${filteredData.length}`
										}
									</span>
								</div>

								<div className="page-size-selector">
									<span>Mostrar:</span>
									<select
										value={pageSize}
										onChange={(e) => handlePageSizeChange(Number(e.target.value))}
										disabled={isLoading}
									>
										<option value="5">5</option>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
										<option value="100">100</option>
									</select>
									<span>por página</span>
								</div>

								<div className="simple-pagination">
									<Button
										icon={<LeftOutlined />}
										onClick={handlePrevPage}
										disabled={currentPage === 1 || isLoading}
									>
										Anterior
									</Button>

									<div className="current-page-indicator">
										Página {currentPage}
									</div>

									<Button
										icon={<RightOutlined />}
										onClick={handleNextPage}
										iconPosition="end"
										disabled={currentPage >= Math.ceil(filteredData.length / pageSize) || isLoading}
									>
										Siguiente
									</Button>
								</div>
							</div>
						</Col>
					)}
				</Row>
			</div>

			<Modal
				open={isModalVisible}
				onCancel={handleModalClose}
				maskClosable={false}
				keyboard={false}
				footer={null}
				width={800}
				style={{ top: 20 }}
				className="custom-modal"
				destroyOnHidden={true}
				centered
			>
				<GeneralServices
					onOrderCreated={handleOrderCreated}
					onCancel={handleModalClose}
				/>
			</Modal>
		</div>
	);
}